<script setup lang="ts">
import type { Swiper } from 'swiper/types';
import type { EntityType, IndexData } from '~/api/types';
import TinkoffBadge from '~/components/common/TinkoffBadge.vue';
import VCreate from '~/components/pages/index/VCreate.vue';
import VCreateWithSuggests from '~/components/pages/index/VCreateWithSuggests.vue';
import { entities } from '~/enitityHelper';
import { getIndexData } from '../../../api/main';

const props = defineProps<{ entity: EntityType; indexData?: IndexData | null }>();
const currentEntityType = ref(props.entity);
const entityMeta = entities[props.entity];
const activeSlideIndex = Object.keys(entities).indexOf(props.entity);
onMounted(() => {
  const swiper = (document.querySelector('.swiper') as Element & { swiper: Swiper })!.swiper;
  swiper.slideTo(activeSlideIndex, 700);
});

const { data: mainInfo } = useAsyncData('main-info', getIndexData);
</script>

<template>
  <div class="promo">
    <div class="promo-wrap">
      <div class="container promo__heading">
        <h1 v-html="entityMeta.createProjectBlock.title" />
        <TinkoffBadge style="margin-bottom: 8px" />
      </div>
      <div class="container-swiper container">
        <Swiper
          :slides-per-view="2.3"
          :space-between="8"
          :breakpoints="{
            768: {
              slidesPerView: 6,
              spaceBetween: 12,
            },
          }"
          class="promo__tabs"
        >
          <SwiperSlide
            v-for="entityType in Object.keys(entities)"
            :key="entityType"
          >
            <NuxtLink
              class="tabs__item"
              :class="{ active: currentEntityType === entityType }"
              :to="entities[entityType as EntityType].link"
            >
              <span class="title">{{ entities[entityType as EntityType].name }}&nbsp;</span>
              <span class="subtitle">{{ entities[entityType as EntityType].description }}&nbsp;</span>
            </NuxtLink>
          </SwiperSlide>
        </Swiper>
      </div>
      <v-create-with-suggests
        v-if="mainInfo?.is_use_suggest"
        :entity="props.entity"
        class="container"
      />
      <v-create
        v-else
        :entity="props.entity"
        class="container"
      />
    </div>
  </div>
</template>

<style lang="scss">
.promo {
  padding: 152px 0 36px 0;
  @include media-breakpoint-down(md) {
    padding: 90px 0 36px 0;
  }

  .promo-wrap {
    margin: 0 auto;
    gap: 60px;

    .promo__heading {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: start;

      @include media-breakpoint-down(lg) {
        flex-direction: column-reverse;
      }

      @include media-breakpoint-down(md) {
        padding: 0 20px 16px 20px;
      }
    }

    .container-swiper {
      @include media-breakpoint-down(sm) {
        &.container {
          max-width: 100vw !important;
          padding: 0 !important;
        }
      }
    }

    .swiper {
      @include media-breakpoint-down(md) {
        padding: 0 20px !important;
      }

      .swiper-wrapper {
        margin: auto auto 24px;

        .swiper-slide {
          max-width: 160px;
          height: auto;

          @include media-breakpoint-down(md) {
            flex-grow: 0;
            max-width: unset;
          }

          &:last-child {
            margin-right: 0 !important;
          }

          .tabs__item {
            margin-bottom: 0;
            height: 100%;
          }
        }
      }
    }

    h1 {
      color: $foreground-contrast;
      text-align: start;
      font-size: 48px;
      text-wrap: balance;
      line-height: 52px;
      margin-bottom: 32px;
      max-width: 800px;
      font-weight: 600;

      @include media-breakpoint-down(md) {
        margin-bottom: 16px;
        font-size: 32px;
        line-height: 36px;
      }
    }

    .promo__tabs {
      .tabs__item {
        display: flex;
        flex-direction: column;

        font-size: 14px;
        padding: 8px 16px;
        background: white;
        border-radius: 12px;
        margin-bottom: 24px;

        &:hover {
          cursor: pointer;
        }

        .title {
          color: $foreground-contrast;
          font-weight: 500;
          line-height: 20px;
        }

        .subtitle {
          color: $foreground-gray;
          line-height: 18px;
        }

        &.active {
          border: none;
          background: #2b2433;

          .title {
            color: white;
            font-weight: 500;
            line-height: 20px;
          }

          .subtitle {
            color: rgba(255, 255, 255, 0.5);
            line-height: 18px;
          }
        }
      }
    }
  }
}
</style>
